"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { IconaFreccia } from "@/app/components/Icone";

/**
 * Una fila di schede che scorre in orizzontale.
 *
 * Esisteva in due copie — una nello scaffale della home, una accanto alla chat
 * — ed erano già divergenti: frecce diverse, barre diverse. Sono lo stesso
 * oggetto, quindi ora è lo stesso codice.
 *
 * Tre cose che la fanno funzionare, e che separate sembrerebbero dettagli:
 *
 * - **le frecce sono a metà altezza sui bordi**, sporgendo di un quarto. Non
 *   di metà: a metà uscivano di 32 pixel mentre la pagina ne ha 16 o 24 di
 *   margine, e quegli 8 pixel di troppo facevano comparire una barra di
 *   scorrimento orizzontale in fondo a tutto il sito;
 * - **la barra di sistema è nascosta** (`.senza-barra`) e sostituita da una
 *   barra blu corta e trascinabile: quella nativa cambia aspetto e ingombro da
 *   un browser all'altro, e su Windows si prende una riga sotto ogni fila;
 * - **la barra segue lo scorrimento, non lo sostituisce**: rotella, dita e
 *   tastiera continuano a funzionare, lei mostra solo dove si è arrivati.
 */

export function Carosello({
  etichetta,
  passo = 380,
  className = "",
  children,
}: {
  /** Serve alle etichette delle frecce: «Indietro in Novità». */
  etichetta: string;
  /** Quanto scorre un clic. Un passo di due schede: una sola per clic fa
   *  sembrare la fila più lunga di quanto sia. */
  passo?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const pista = useRef<HTMLUListElement>(null);
  const binario = useRef<HTMLDivElement>(null);
  // Quanto è lunga la barra e dove sta, in percentuale del binario. Larghezza
  // zero significa che la fila ci sta tutta e la barra non serve.
  const [barra, setBarra] = useState({ larghezza: 0, sinistra: 0 });

  const misura = useCallback(() => {
    const el = pista.current;
    if (!el) return;
    const scorribile = el.scrollWidth - el.clientWidth;
    if (scorribile <= 1) {
      setBarra({ larghezza: 0, sinistra: 0 });
      return;
    }
    const larghezza = (el.clientWidth / el.scrollWidth) * 100;
    setBarra({
      larghezza,
      sinistra: (el.scrollLeft / scorribile) * (100 - larghezza),
    });
  }, []);

  useEffect(() => {
    const el = pista.current;
    if (!el) return;
    // `ResizeObserver` fa anche la prima misura, appena osserva: così non serve
    // un setState nel corpo dell'effetto, che sarebbe un render in più.
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(el);
    el.addEventListener("scroll", misura, { passive: true });
    return () => {
      osservatore.disconnect();
      el.removeEventListener("scroll", misura);
    };
  }, [misura]);

  const scorri = (verso: 1 | -1) =>
    pista.current?.scrollBy({ left: verso * passo, behavior: "smooth" });

  /** Trascinamento della barra. */
  function afferra(e: React.PointerEvent<HTMLDivElement>) {
    const el = pista.current;
    const traccia = binario.current;
    if (!el || !traccia || barra.larghezza === 0) return;

    e.preventDefault();
    const partenzaX = e.clientX;
    const partenzaScroll = el.scrollLeft;
    const scorribile = el.scrollWidth - el.clientWidth;
    // Il pollice non percorre tutto il binario: percorre quello che avanza
    // oltre la sua stessa lunghezza. Senza questa correzione il contenuto
    // scorre più del dito e la barra scappa via.
    const corsa = traccia.clientWidth * (1 - barra.larghezza / 100);
    if (corsa <= 0) return;

    // Lo scorrimento morbido va spento mentre si trascina, o insegue il dito
    // con un ritardo che sembra un impuntamento.
    el.style.scrollBehavior = "auto";

    const muovi = (ev: PointerEvent) => {
      el.scrollLeft = partenzaScroll + ((ev.clientX - partenzaX) / corsa) * scorribile;
    };
    const molla = () => {
      el.style.scrollBehavior = "";
      window.removeEventListener("pointermove", muovi);
      window.removeEventListener("pointerup", molla);
      window.removeEventListener("pointercancel", molla);
    };
    window.addEventListener("pointermove", muovi);
    window.addEventListener("pointerup", molla);
    window.addEventListener("pointercancel", molla);
  }

  return (
    <div className={className}>
      <div className="relative">
        <ul
          ref={pista}
          className="senza-barra flex gap-3 overflow-x-auto scroll-smooth pb-1"
        >
          {children}
        </ul>

        {barra.larghezza > 0 && (
          <>
            <button
              type="button"
              onClick={() => scorri(-1)}
              aria-label={`Indietro in ${etichetta}`}
              className="absolute left-0 top-1/2 z-10 -translate-x-1/4 -translate-y-1/2 p-1 text-accent drop-shadow transition hover:opacity-70"
            >
              <IconaFreccia className="size-16" />
            </button>
            <button
              type="button"
              onClick={() => scorri(1)}
              aria-label={`Avanti in ${etichetta}`}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/4 p-1 text-accent drop-shadow transition hover:opacity-70"
            >
              <IconaFreccia className="size-16 rotate-180" />
            </button>
          </>
        )}
      </div>

      {/* `touch-none` serve, o il browser legge il trascinamento come uno
          scorrimento della pagina e la barra non si muove. */}
      {barra.larghezza > 0 && (
        <div
          ref={binario}
          className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line"
          aria-hidden
        >
          <div
            onPointerDown={afferra}
            className="h-full cursor-grab touch-none rounded-full bg-accent active:cursor-grabbing"
            style={{
              width: `${barra.larghezza}%`,
              marginLeft: `${barra.sinistra}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}
